'use client';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Github, Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useState } from 'react';

// Background wrapper component
const PageWrapper = ({ children }: { children: React.ReactNode }) => (
  <div
    className="min-h-screen w-full bg-cover bg-left lg:bg-center bg-no-repeat flex items-center justify-start"
    style={{ backgroundImage: "url('/images/background.svg')" }}
  >
    <div className="w-full max-w-[520px] mx-4 sm:mx-8 lg:ml-32 py-8 lg:py-12">
      {children}
    </div>
  </div>
);

// Logo component with fallback
const Logo = () => (
  <div className="flex items-center justify-center mb-6">
    <img
      src="/images/Impulsum-logo.png"
      alt="Impulsum"
      className="h-12 w-auto"
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        target.parentElement!.innerHTML = `
          <svg width="200" height="60" viewBox="0 0 400 120" class="h-12">
            <defs>
              <linearGradient id="impulsum-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#75509B" />
                <stop offset="100%" stop-color="#F58020" />
              </linearGradient>
            </defs>
            <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
                  fill="url(#impulsum-gradient)" 
                  style="font-family: system-ui; font-size: 48px; font-weight: 700; font-style: italic;">
              Impulsum
            </text>
          </svg>
        `;
      }}
    />
  </div>
);

// Reusable Alert Message Component
const AlertMessage = ({ type, message }: { type: 'error' | 'success'; message: string }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div className={`px-4 py-3 rounded-md text-sm ${isError
      ? 'bg-red-900/20 border border-red-900/50 text-red-400'
      : 'bg-emerald-900/20 border border-emerald-900/50 text-emerald-400'
      }`}>
      {message}
    </div>
  );
};

// Reusable Form Field Component
const FormField = ({
  id,
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  disabled,
  required,
  showToggle,
  isVisible,
  onToggleVisibility,
  error,
}: {
  id: string;
  label: string;
  type?: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  showToggle?: boolean;
  isVisible?: boolean;
  onToggleVisibility?: () => void;
  error?: string;
}) => (
  <div className="flex flex-col gap-2">
    <Label htmlFor={id} className="text-white font-semibold text-sm tracking-[0.01em]">
      {label}
    </Label>
    <div className="relative">
      <Input
        id={id}
        type={showToggle ? (isVisible ? 'text' : 'password') : type}
        placeholder={placeholder}
        className={`h-[42px] rounded-[5px] bg-white text-black border-0 pl-3.5 font-medium text-sm tracking-[0.01em] placeholder:text-black/40 ${showToggle ? 'pr-10' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
      />
      {showToggle && (
        <button
          type="button"
          onClick={onToggleVisibility}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-black/60 hover:text-black transition-colors"
          tabIndex={-1}
        >
          {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
    </div>
    {error && <p className="text-xs text-red-400">{error}</p>}
  </div>
);

// OAuth Button Component
const OAuthButton = ({
  onClick,
  icon,
  label,
  disabled,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-full h-[42px] flex items-center justify-center border border-muted rounded-[10px] text-white text-sm font-medium transition-all hover:bg-white/10 disabled:opacity-50"
  >
    <span className="flex items-center gap-3 w-[200px]">
      <span className="w-5 flex justify-center shrink-0">{icon}</span>
      {label}
    </span>
  </button>
);

// OAuth Icons as constants
const GoogleIcon = (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const JiraIcon = (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
    <path d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7c0 2.4 1.94 4.34 4.34 4.35V2.84A.84.84 0 0 0 21.16 2H11.53Z" fill="#2684FF" />
    <path d="M6.77 6.8a4.36 4.36 0 0 0 4.34 4.36h1.8v1.7a4.36 4.36 0 0 0 4.33 4.35V7.66a.84.84 0 0 0-.83-.85H6.77Z" fill="url(#jira-grad-1)" />
    <path d="M2 11.6a4.35 4.35 0 0 0 4.35 4.34h1.78v1.71A4.35 4.35 0 0 0 12.47 22v-9.56a.84.84 0 0 0-.85-.84H2Z" fill="url(#jira-grad-2)" />
    <defs>
      <linearGradient id="jira-grad-1" x1="12.87" y1="6.81" x2="9.09" y2="10.72" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0052CC" />
        <stop offset="1" stopColor="#2684FF" />
      </linearGradient>
      <linearGradient id="jira-grad-2" x1="8.11" y1="11.6" x2="4.2" y2="15.64" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0052CC" />
        <stop offset="1" stopColor="#2684FF" />
      </linearGradient>
    </defs>
  </svg>
);

const LinearIcon = (
  <svg className="h-5 w-5" viewBox="0 0 100 100" fill="currentColor" aria-label="Linear Logo">
    <path d="M1.22541 61.5228c-.2225-.9485.90748-1.5459 1.59638-.857L39.3342 97.1782c.6889.6889.0915 1.8189-.857 1.5964C20.0515 94.4522 5.54779 79.9485 1.22541 61.5228ZM.00189135 46.8891c-.01764375.2833.08887215.5599.28957165.7606L52.3503 99.7085c.2007.2007.4773.3075.7606.2896 2.3692-.1476 4.6938-.46 6.9624-.9259.7645-.157 1.0301-1.0963.4782-1.6481L2.57595 39.4485c-.55186-.5519-1.49117-.2863-1.648174.4782-.465915 2.2686-.77832 4.5932-.92588465 6.9624ZM4.21093 29.7054c-.16649.3738-.08169.8106.20765 1.1l64.77602 64.776c.2894.2894.7262.3742 1.1.2077 1.7861-.7956 3.5171-1.6927 5.1855-2.684.5521-.328.6373-1.0867.1832-1.5407L8.43566 24.3367c-.45409-.4541-1.21271-.3689-1.54074.1832-.99132 1.6684-1.88843 3.3994-2.68399 5.1855ZM12.6587 18.074c-.3701-.3701-.393-.9637-.0443-1.3541C21.7795 6.45931 35.1114 0 49.9519 0 77.5927 0 100 22.4073 100 50.0481c0 14.8405-6.4593 28.1724-16.7199 37.3375-.3903.3487-.984.3258-1.3542-.0443L12.6587 18.074Z" />
  </svg>
);

// Gradient Icon Circle Component (for verification screens)
const GradientIconCircle = ({ children, size = 'md' }: { children: React.ReactNode; size?: 'sm' | 'md' }) => (
  <div className={`bg-gradient-to-r from-brand-purple to-brand-orange rounded-full flex items-center justify-center ${size === 'sm' ? 'w-11 h-11' : 'w-16 h-16'
    }`}>
    {children}
  </div>
);

// Email Icon
const EmailIcon = ({ size = 'md' }: { size?: 'sm' | 'md' }) => (
  <svg className={size === 'sm' ? 'w-5 h-5 text-white' : 'w-8 h-8 text-white'} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 6L12 13L2 6" />
  </svg>
);

// Key Icon
const KeyIcon = ({ size = 'md' }: { size?: 'sm' | 'md' }) => (
  <svg className={size === 'sm' ? 'w-5 h-5 text-white' : 'w-8 h-8 text-white'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
  </svg>
);

// Password Strength Indicator Component
const PasswordStrengthIndicator = ({ passwordStrength }: {
  passwordStrength: { minLength: boolean; hasUppercase: boolean; hasLowercase: boolean; hasNumber: boolean; hasSpecialChar: boolean; isStrong: boolean }
}) => {
  const requirements = [
    { key: 'minLength', label: 'At least 8 characters' },
    { key: 'hasUppercase', label: 'One uppercase letter (A-Z)' },
    { key: 'hasLowercase', label: 'One lowercase letter (a-z)' },
    { key: 'hasNumber', label: 'One number (0-9)' },
    { key: 'hasSpecialChar', label: 'One special character (!@#$%^&*)' },
  ] as const;

  return (
    <div className="mt-2 p-3 bg-secondary/50 border border-muted rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-white">Password Strength</span>
        <span className={`text-xs font-medium ${passwordStrength.isStrong ? 'text-emerald-400' : 'text-brand-orange'}`}>
          {passwordStrength.isStrong ? 'Strong' : 'Weak'}
        </span>
      </div>
      <div className="space-y-1">
        {requirements.map((item) => (
          <div key={item.key} className={`flex items-center text-xs ${passwordStrength[item.key] ? 'text-emerald-400' : 'text-muted-foreground'}`}>
            <div className={`w-3 h-3 rounded-full mr-2 flex items-center justify-center ${passwordStrength[item.key] ? 'bg-emerald-500' : 'bg-muted'}`}>
              {passwordStrength[item.key] && (
                <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};

// Back to Login Link Component
const BackToLoginLink = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-sm text-muted-foreground hover:text-white underline text-center transition-colors"
  >
    Back to Login
  </button>
);

// Social Login Buttons Group
const SocialLoginButtons = ({
  onGoogleLogin,
  onGithubLogin,
  isLoading,
}: {
  onGoogleLogin: () => void;
  onGithubLogin: () => void;
  isLoading: boolean;
}) => (
  <div className="flex flex-col gap-2.5">
    <OAuthButton onClick={onGoogleLogin} icon={GoogleIcon} label="Continue with Google" disabled={isLoading} />
    <OAuthButton onClick={onGithubLogin} icon={<Github className="h-5 w-5" />} label="Continue with GitHub" disabled={isLoading} />
    <OAuthButton onClick={() => window.alert('Jira integration coming soon!')} icon={JiraIcon} label="Connect with Jira" disabled={isLoading} />
    <OAuthButton onClick={() => window.alert('Linear integration coming soon!')} icon={LinearIcon} label="Connect with Linear" disabled={isLoading} />
  </div>
);

// ============== Main Component ==============

export default function Home() {
  const { signIn, signUp, signInWithGoogle, signInWithGithub, loading, resetPassword, resendVerification } = useAuth();

  // Screen state
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [showForgotPasswordScreen, setShowForgotPasswordScreen] = useState(false);
  const [showPasswordResetSentScreen, setShowPasswordResetSentScreen] = useState(false);

  // Form state
  const [userEmail, setUserEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [touched, setTouched] = useState({ firstName: false, lastName: false });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    isStrong: false,
  });

  //  Handlers

  const validatePasswordStrength = (password: string) => {
    const checks = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      isStrong: false,
    };
    checks.isStrong = Object.keys(checks).slice(0, -1).every((key) => checks[key as keyof typeof checks]);
    return checks;
  };

  const handlePasswordChange = (value: string) => {
    setFormData((prev) => ({ ...prev, password: value }));
    if (isSignupMode) setPasswordStrength(validatePasswordStrength(value));
  };

  const resetFormState = () => {
    setError('');
    setSuccess('');
    setFormData({ email: userEmail, password: '', confirmPassword: '', firstName: '', lastName: '' });
    setPasswordStrength({ minLength: false, hasUppercase: false, hasLowercase: false, hasNumber: false, hasSpecialChar: false, isStrong: false });
  };

  const handleBackToLogin = () => {
    setShowVerificationScreen(false);
    setShowForgotPasswordScreen(false);
    setShowPasswordResetSentScreen(false);
    setIsSignupMode(false);
    resetFormState();
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const { error } = await signIn(formData.email, formData.password);
    if (error) setError(error.message);
    else window.location.href = '/dashboard?login_success=true';
    setIsLoading(false);
  };

  const handleSignUp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!isSignupMode) {
      setIsSignupMode(true);
      setError('');
      return;
    }

    const firstNameTrimmed = formData.firstName.trim();
    const lastNameTrimmed = formData.lastName.trim();
    if (!touched.firstName || !touched.lastName) setTouched({ firstName: true, lastName: true });

    if (!formData.email || !formData.password || !firstNameTrimmed || !lastNameTrimmed) {
      setError('Please fill in all required fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!passwordStrength.isStrong) {
      setError('Password does not meet security requirements');
      return;
    }

    setIsLoading(true);
    setError('');
    const { error } = await signUp(formData.email, formData.password);
    if (error) setError(error.message);
    else {
      setUserEmail(formData.email);
      setShowVerificationScreen(true);
      setIsSignupMode(false);
    }
    setIsLoading(false);
  };

  const handleGoogleLogin = async () => {
    setError('');
    const { error } = await signInWithGoogle();
    if (error) setError(error.message);
  };

  const handleGithubLogin = async () => {
    setError('');
    const { error } = await signInWithGithub();
    if (error) setError(error.message);
  };

  const handleResendVerification = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      if (!userEmail) { setError('Please enter your email address'); return; }
      const { error } = await resendVerification(userEmail);
      if (error) setError(error.message || 'Failed to resend verification email');
      else setSuccess('Verification email sent! Please check your inbox and spam folder.');
    } catch { setError('Failed to resend verification email'); }
    finally { setIsLoading(false); }
  };

  const handleForgotPassword = () => {
    setShowForgotPasswordScreen(true);
    setError('');
    if (formData.email) setUserEmail(formData.email);
  };

  const handleSendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    if (!userEmail) { setError('Please enter your email address'); setIsLoading(false); return; }
    const { error } = await resetPassword(userEmail);
    if (error) setError(error.message);
    else { setShowForgotPasswordScreen(false); setShowPasswordResetSentScreen(true); }
    setIsLoading(false);
  };

  const handleResendResetEmail = async () => {
    setIsLoading(true);
    setError('');
    const { error } = await resetPassword(userEmail);
    if (error) setError(error.message);
    else setSuccess('Password reset email sent again! Please check your inbox.');
    setIsLoading(false);
  };

  //  Loading State 
  if (loading) {
    return (
      <PageWrapper>
        <div className="flex items-center justify-center h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      </PageWrapper>
    );
  }

  // Password Reset Sent Screen 
  if (showPasswordResetSentScreen) {
    return (
      <PageWrapper>
        <div className="flex flex-col gap-3.5 p-4 sm:p-6">
          <Logo />
          <h1 className="text-2xl sm:text-[32px] font-black text-white text-center leading-tight tracking-[0.01em]">Check your Email</h1>
          <div className="w-full flex flex-col items-center gap-2.5 p-4 bg-secondary rounded-[15px]">
            <GradientIconCircle size="sm"><KeyIcon size="sm" /></GradientIconCircle>
            <p className="text-white font-semibold text-base text-center tracking-[0.01em]">Password reset email sent to:</p>
            <p className="text-white text-sm text-center tracking-[0.01em] break-all">{userEmail}</p>
            <p className="text-sm text-white text-center tracking-[0.01em] max-w-[396px]">Please check your inbox and click the reset link to create a new password. Don&apos;t forget to check your spam folder!</p>
          </div>
          <AlertMessage type="error" message={error} />
          <AlertMessage type="success" message={success} />
          <div className="flex flex-col items-center gap-2.5">
            <button onClick={handleResendResetEmail} disabled={isLoading} className="h-9 px-4 bg-gradient-to-r from-brand-purple to-brand-orange rounded-[10px] flex items-center justify-center disabled:opacity-50">
              {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin text-white" /><span className="text-white font-semibold text-base tracking-[0.01em]">Sending...</span></> : <span className="text-white font-semibold text-base tracking-[0.01em]">Resend Reset Email</span>}
            </button>
            <BackToLoginLink onClick={handleBackToLogin} />
          </div>
          <p className="text-xs text-white text-center tracking-[0.01em]">The reset link will expire in 24 hours for security.</p>
        </div>
      </PageWrapper>
    );
  }

  //  Forgot Password Screen 
  if (showForgotPasswordScreen) {
    return (
      <PageWrapper>
        <div className="flex flex-col gap-4 p-4 sm:p-6">
          <Logo />
          <h1 className="text-2xl sm:text-[32px] font-black text-white text-center leading-tight tracking-[0.01em]">Reset Your Password</h1>
          <p className="text-sm text-muted-foreground text-center">Enter your email address and we&apos;ll send you a link to reset your password</p>
          <AlertMessage type="error" message={error} />
          <form onSubmit={handleSendResetEmail} className="flex flex-col gap-4">
            <FormField id="reset-email" label="Email" type="email" placeholder="info@impulsum.me" value={userEmail} onChange={setUserEmail} required disabled={isLoading} />
            <Button type="submit" className="w-full h-[42px] rounded-[10px]" disabled={isLoading}>
              {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending Reset Email...</> : 'Send Reset Email'}
            </Button>
          </form>
          <button type="button" onClick={handleBackToLogin} className="text-sm text-muted-foreground hover:text-white underline text-center">← Back to Login</button>
        </div>
      </PageWrapper>
    );
  }

  //  Verification Screen 
  if (showVerificationScreen) {
    return (
      <PageWrapper>
        <div className="flex flex-col gap-3.5 p-4 sm:p-6">
          <Logo />
          <h1 className="text-2xl sm:text-[32px] font-black text-white text-center leading-tight tracking-[0.01em]">Check your Email</h1>
          <div className="w-full flex flex-col items-center gap-2.5 p-4 bg-secondary rounded-[15px]">
            <GradientIconCircle size="sm"><EmailIcon size="sm" /></GradientIconCircle>
            <p className="text-white font-semibold text-base text-center tracking-[0.01em]">Verification email sent to:</p>
            <p className="text-white text-sm text-center tracking-[0.01em] break-all">{userEmail}</p>
            <p className="text-sm text-white text-center tracking-[0.01em] max-w-[396px]">Please check your inbox and click the verification link to activate your account. Don&apos;t forget to check your spam folder!</p>
          </div>
          <AlertMessage type="error" message={error} />
          <AlertMessage type="success" message={success} />
          <div className="flex flex-col items-center gap-2.5">
            <button onClick={handleResendVerification} disabled={isLoading} className="h-9 px-4 bg-gradient-to-r from-brand-purple to-brand-orange rounded-[10px] flex items-center justify-center disabled:opacity-50">
              {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin text-white" /><span className="text-white font-semibold text-base tracking-[0.01em]">Sending...</span></> : <span className="text-white font-semibold text-base tracking-[0.01em]">Resend Verification Email</span>}
            </button>
            <BackToLoginLink onClick={handleBackToLogin} />
          </div>
          <p className="text-xs text-white text-center tracking-[0.01em]">Once verified, you&apos;ll be able to sign in with your new account.</p>
        </div>
      </PageWrapper>
    );
  }

  //  Main Login/Signup Form 
  return (
    <PageWrapper>
      <div className="flex flex-col gap-3.5 p-4 sm:p-6">
        <Logo />
        <h1 className="text-2xl sm:text-[32px] font-black text-white text-center leading-tight tracking-[0.01em]">
          {isSignupMode ? 'Create Account' : 'Log in'}
        </h1>
        {isSignupMode && <p className="text-sm text-muted-foreground text-center">Join Impulsum and start automating your workflows</p>}

        <AlertMessage type="error" message={error} />

        <form onSubmit={isSignupMode ? handleSignUp : handleEmailLogin} className="flex flex-col gap-4">
          <FormField id="email" label="Email" type="email" placeholder="info@impulsum.me" value={formData.email} onChange={(v) => setFormData({ ...formData, email: v })} required disabled={isLoading} />

          <div className="flex flex-col gap-2">
            <FormField
              id="password"
              label="Password"
              placeholder="••••••••••••"
              value={formData.password}
              onChange={handlePasswordChange}
              required
              disabled={isLoading}
              showToggle
              isVisible={showPassword}
              onToggleVisibility={() => setShowPassword(!showPassword)}
            />
            {!isSignupMode && (
              <div className="flex justify-end">
                <button type="button" className="text-sm text-muted-foreground hover:text-white underline" onClick={handleForgotPassword}>Forgot password?</button>
              </div>
            )}
            {isSignupMode && formData.password && <PasswordStrengthIndicator passwordStrength={passwordStrength} />}
          </div>

          {isSignupMode && (
            <>
              <FormField
                id="confirmPassword"
                label="Repeat Password"
                placeholder="••••••••••••"
                value={formData.confirmPassword}
                onChange={(v) => setFormData({ ...formData, confirmPassword: v })}
                required
                disabled={isLoading}
                showToggle
                isVisible={showConfirmPassword}
                onToggleVisibility={() => setShowConfirmPassword(!showConfirmPassword)}
                error={formData.confirmPassword && formData.password !== formData.confirmPassword ? 'Passwords do not match' : undefined}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  id="firstName"
                  label="First Name"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={(v) => setFormData({ ...formData, firstName: v })}
                  required
                  disabled={isLoading}
                  error={touched.firstName && !formData.firstName.trim() ? 'First name is required' : undefined}
                />
                <FormField
                  id="lastName"
                  label="Last Name"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={(v) => setFormData({ ...formData, lastName: v })}
                  required
                  disabled={isLoading}
                  error={touched.lastName && !formData.lastName.trim() ? 'Last name is required' : undefined}
                />
              </div>
            </>
          )}

          <Button
            type="submit"
            className="w-full h-[42px] rounded-[10px] text-sm font-bold"
            disabled={isLoading || (isSignupMode && (!passwordStrength.isStrong || !formData.firstName.trim() || !formData.lastName.trim()))}
          >
            {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{isSignupMode ? 'Creating account...' : 'Signing in...'}</> : isSignupMode ? 'Create Account' : 'Log in'}
          </Button>
        </form>

        {!isSignupMode && <SocialLoginButtons onGoogleLogin={handleGoogleLogin} onGithubLogin={handleGithubLogin} isLoading={isLoading} />}

        <div className="flex flex-col items-center gap-3.5">
          <p className="text-sm text-white text-center">
            {isSignupMode ? (
              <>Already have an account?{' '}<button type="button" className="text-white hover:underline font-medium" onClick={() => { setIsSignupMode(false); resetFormState(); }}>Sign in here</button></>
            ) : (
              <>Don&apos;t have account?{' '}<button type="button" className="text-white hover:underline font-medium" onClick={handleSignUp}>Register</button></>
            )}
          </p>
          <button type="button" className="text-sm text-white hover:text-white/80 underline" onClick={() => window.alert('SSO integration coming soon!')}>Continue with SSO</button>
        </div>
      </div>
    </PageWrapper>
  );
}
